import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, MapPin, Lock, Package, Edit2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

function ProfilePage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const [editingAddress, setEditingAddress] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [addressForm, setAddressForm] = useState({
    company_name: user?.company_name || '',
    contact_name: user?.contact_name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    postal_code: user?.postal_code || ''
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  if (!user) return null;

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/profile', addressForm);
      if (refreshUser) await refreshUser();
      toast.success(t('profileUpdated'));
      setEditingAddress(false);
    } catch (error) {
      toast.error(t('errorSaving'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(t('passwordsNotMatch'));
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error(t('passwordTooShort'));
      return;
    }
    
    setLoading(true);
    try {
      await api.put('/profile/password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success(t('passwordChanged'));
      setChangingPassword(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorChangingPassword'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 lg:px-12 py-8">
      <div className="flex items-center gap-3 mb-8">
        <User className="h-8 w-8" />
        <h1 className="text-3xl md:text-4xl font-heading font-bold uppercase" data-testid="profile-title">
          {t('profile')}
        </h1>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Quick Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Button
            variant="outline"
            className="h-auto py-4 justify-start"
            onClick={() => navigate('/orders')}
            data-testid="go-to-orders"
          >
            <Package className="h-5 w-5 me-3" />
            <div className="text-start">
              <div className="font-semibold">{t('orders')}</div>
              <div className="text-xs text-muted-foreground">{t('viewOrderHistory')}</div>
            </div>
          </Button>
          
          <Button
            variant="outline"
            className="h-auto py-4 justify-start"
            onClick={() => navigate('/favorites')}
            data-testid="go-to-favorites"
          >
            <svg className="h-5 w-5 me-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            <div className="text-start">
              <div className="font-semibold">{t('favorites')}</div>
              <div className="text-xs text-muted-foreground">{t('savedProducts')}</div>
            </div>
          </Button>
        </div>

        {/* Address Section */}
        <div className="bg-white border border-border rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              <h2 className="text-xl font-heading font-bold">{t('addressAndContact')}</h2>
            </div>
            {!editingAddress && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingAddress(true)}
                data-testid="edit-address-btn"
              >
                <Edit2 className="h-4 w-4 me-1" />
                {t('edit')}
              </Button>
            )}
          </div>

          {editingAddress ? (
            <form onSubmit={handleAddressSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name">{t('companyName')}</Label>
                  <Input
                    id="company_name"
                    value={addressForm.company_name}
                    onChange={(e) => setAddressForm({...addressForm, company_name: e.target.value})}
                    className="mt-1"
                    data-testid="company-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name">{t('contactName')}</Label>
                  <Input
                    id="contact_name"
                    value={addressForm.contact_name}
                    onChange={(e) => setAddressForm({...addressForm, contact_name: e.target.value})}
                    className="mt-1"
                    data-testid="contact-name-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="phone">{t('phone')}</Label>
                <Input
                  id="phone"
                  value={addressForm.phone}
                  onChange={(e) => setAddressForm({...addressForm, phone: e.target.value})}
                  className="mt-1"
                  data-testid="phone-input"
                />
              </div>
              
              <div>
                <Label htmlFor="address">{t('address')}</Label>
                <Input
                  id="address"
                  value={addressForm.address}
                  onChange={(e) => setAddressForm({...addressForm, address: e.target.value})}
                  className="mt-1"
                  data-testid="address-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="postal_code">{t('postalCode')}</Label>
                  <Input
                    id="postal_code"
                    value={addressForm.postal_code}
                    onChange={(e) => setAddressForm({...addressForm, postal_code: e.target.value})}
                    className="mt-1"
                    data-testid="postal-code-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city">{t('city')}</Label>
                  <Input
                    id="city"
                    value={addressForm.city}
                    onChange={(e) => setAddressForm({...addressForm, city: e.target.value})}
                    className="mt-1"
                    data-testid="city-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={loading} data-testid="save-address-btn">
                  <Save className="h-4 w-4 me-1" />
                  {loading ? t('saving') : t('save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingAddress(false);
                    setAddressForm({
                      company_name: user.company_name,
                      contact_name: user.contact_name,
                      phone: user.phone,
                      address: user.address,
                      city: user.city,
                      postal_code: user.postal_code
                    });
                  }}
                  data-testid="cancel-edit-btn"
                >
                  <X className="h-4 w-4 me-1" />
                  {t('cancel')}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-muted-foreground">{t('companyName')}</span>
                <p className="font-semibold" data-testid="company-name">{user.company_name}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">{t('contactName')}</span>
                <p className="font-semibold" data-testid="contact-name">{user.contact_name}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">{t('email')}</span>
                <p className="font-semibold" data-testid="email">{user.email}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">{t('phone')}</span>
                <p className="font-semibold" data-testid="phone">{user.phone}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">{t('address')}</span>
                <p className="font-semibold" data-testid="address">
                  {user.address}<br />
                  {user.postal_code} {user.city}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Password Section */}
        <div className="bg-white border border-border rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              <h2 className="text-xl font-heading font-bold">{t('passwordSection')}</h2>
            </div>
            {!changingPassword && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setChangingPassword(true)}
                data-testid="change-password-btn"
              >
                <Edit2 className="h-4 w-4 me-1" />
                {t('change')}
              </Button>
            )}
          </div>

          {changingPassword ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <Label htmlFor="current_password">{t('currentPassword')}</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                  required
                  className="mt-1"
                  data-testid="current-password-input"
                />
              </div>
              
              <div>
                <Label htmlFor="new_password">{t('newPassword')}</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  required
                  minLength={6}
                  className="mt-1"
                  data-testid="new-password-input"
                />
              </div>
              
              <div>
                <Label htmlFor="confirm_password">{t('confirmPassword')}</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                  required
                  className="mt-1"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={loading} data-testid="save-password-btn">
                  <Save className="h-4 w-4 me-1" />
                  {loading ? t('saving') : t('changePassword')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setChangingPassword(false);
                    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                  }}
                  data-testid="cancel-password-btn"
                >
                  <X className="h-4 w-4 me-1" />
                  {t('cancel')}
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-muted-foreground">
              ••••••••
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
